import loadable from '@loadable/component';
import { gtmDatakey } from '@utils/GoogleTagManager';
import { SelfExcludedCheck } from '@v2/components/self-excluded-check/self-excluded-check.tsx';
import { Waiting } from '@v3/configurable-components/waiting/waiting.tsx';
import { useEffect, useMemo, useRef } from 'react';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import {
  ComponentName,
  ComponentSettings,
  PagesName,
} from '../../../constants';
import { useRoutePath } from '../../../hooks';
import { useAuth } from '../../../hooks/useAuth';
import { useConfig } from '../../../hooks/useConfig';
import useGTM from '../../../hooks/useGTM';
import { useI18n } from '../../../hooks/useI18n';
import { useModal } from '../../../hooks/useModal';
import { useUIConfig } from '../../../hooks/useUIConfig';
import { ConfigLoaded } from '../../../types/Config';
import { KYC_VALIDATOR_STATUS } from '../../../types/UserStatus';
import ErrorBoundary from '../../ErrorBoundary';
import CookieConsent from '../components/CookieConsent';
import GeocomplyAlert from '../components/header/GeoComplyAlert.tsx';
import NotFoundPage from '../pages/notFoundPage';
import PageContent from './PageContent.tsx';
import PageHeader from './PageHeader';

const LoadablePageFooter = loadable(() => import('./PageColumnFooter'));

const LoadableDynamicAlertBanner = loadable(
  () => import('../components/header/DynamicAlertBanner'),
);
let prevPathname: string | null = null;

const PageLayout = ({ children }) => {
  const sendDataToGTM = useGTM();

  const { t, jsxT } = useI18n();
  const { pathname } = useLocation();
  const { enableModal } = useModal();
  const { headerNav } = useUIConfig();
  const { sidebars, routes, locale, configLoaded } = useConfig((prev, next) => {
    const configLoaded = prev.configLoaded === next.configLoaded;
    const localeEqual = prev.locale === next.locale;
    return configLoaded && localeEqual;
  });
  const { user, signout } = useAuth();
  const history = useHistory();
  const sidebar = useMemo(
    () =>
      !user.loading &&
      sidebars?.find(sidebar =>
        sidebar.some(
          link =>
            !link.onlyLink &&
            (pathname.includes(link.link) ||
              link.children?.some(link => pathname.includes(link.link))),
        ),
      ),
    [sidebars, pathname, user.loading],
  );
  const route = useMemo(
    () => routes.find(route => !!matchPath(pathname, route)),
    [routes, pathname],
  );
  const location = useLocation();
  const loginPage = useRoutePath(PagesName.LoginPage);
  const activationPage = useRoutePath(PagesName.RegisterActivationPage);
  const casinoPage = useRoutePath(PagesName.CasinoPage);

  const validatorNotOk = [
    KYC_VALIDATOR_STATUS.CanPlayAndShouldUpdatePersonalData,
    KYC_VALIDATOR_STATUS.ShouldUpdatePersonalDataOnly,
    KYC_VALIDATOR_STATUS.ShouldUpdatePersonalDataLimitedAttempts,
    KYC_VALIDATOR_STATUS.ShouldUploadDocumentForKyc,
    KYC_VALIDATOR_STATUS.ForceDocument,
  ].includes(user.validator_status || 0);

  useEffect(() => {
    if (
      user.logged_in &&
      location.pathname !== loginPage &&
      location.pathname !== activationPage &&
      !!!user.id
    ) {
      signout();
    }
  }, [location.pathname]);

  useEffect(() => {
    if (
      locale &&
      configLoaded === ConfigLoaded.Loaded &&
      route?.id !== PagesName.LocaleSelectPage &&
      !user.loading
    ) {
      process.nextTick(() =>
        sendDataToGTM({
          [gtmDatakey('VirtualUrl')]: `/${locale}${pathname}`,
          [gtmDatakey('VirtualTitle')]: route ? `${t(route.name)}` : '',
          event: 'VirtualPageView',
        }),
      );
    }
  }, [pathname, user.loading, configLoaded]);
  useEffect(() => {
    if (prevPathname && window.scrollY)
      sessionStorage.setItem(
        `route-${prevPathname}`,
        window.scrollY.toString(),
      );
    const savedScroll =
      (history.action === 'POP' &&
        sessionStorage.getItem(`route-${pathname}`)) ||
      0;
    window.scrollTo(0, Number(savedScroll));
    prevPathname = pathname;
  }, [pathname]);

  useEffect(() => {
    const localeSelectRoute = routes.find(
      route => route.id === PagesName.LocaleSelectPage,
    );
    if (
      configLoaded !== ConfigLoaded.Loading &&
      !locale &&
      localeSelectRoute &&
      pathname !== localeSelectRoute.path
    ) {
      history.push(localeSelectRoute.path);
    }
  }, [locale, routes]);

  const { showValidatorStatusBanner } = ComponentSettings!;

  const KYC_MESSAGGE =
    user.validator_status === KYC_VALIDATOR_STATUS.ShouldAnswerKBA
      ? `validator_status_message_${user.validator_status}`
      : !window.__config__.componentSettings?.documentUpload && validatorNotOk
        ? `validation_failed_body_ideology`
        : `validator_status_message_${user.validator_status}`;

  const isFullScreen = () => {
    if (!user?.logged_in) {
      return true;
    }

    if (user?.logged_in) {
      if (
        route?.id === PagesName.DepositPage ||
        route?.id === PagesName.WithdrawalPage ||
        route?.id === PagesName.PromotionsPage ||
        route?.id === PagesName.PersonalInfoPage ||
        route?.id === PagesName.CommunicationPreferencesPage ||
        route?.id === PagesName.ChangePasswordPage ||
        route?.id === PagesName.TransactionsPage ||
        route?.id === PagesName.TransactionsSummaryPage ||
        route?.id === PagesName.BonusesPage ||
        route?.id === PagesName.CasinoBetsPage ||
        route?.id === PagesName.LimitsPage ||
        route?.id === PagesName.RequiredDocuments ||
        route?.id === PagesName.ContactUsPage ||
        route?.id === PagesName.SessionOverviewPage ||
        route?.id === PagesName.ContactUsPage ||
        route?.id === PagesName.TemplatePage ||
        route?.id === PagesName.TaxPage ||
        route?.id === PagesName.CloseAccountPage
      ) {
        return false;
      } else {
        return true;
      }
    }
  };
  if (configLoaded === ConfigLoaded.Loading) {
    return <Waiting />;
  }

  if (
    route?.id === PagesName.DocUploadsReturnPage ||
    route?.id === PagesName.DepositResponse
  ) {
    return children;
  }

  return (
    <>
      <ErrorBoundary>
        <PageHeader />
      </ErrorBoundary>
      {user.logged_in &&
        user?.validator_status === KYC_VALIDATOR_STATUS.Success &&
        !!ComponentSettings?.header?.geoComplyStatusAlert && <GeocomplyAlert />}

      {showValidatorStatusBanner && user.logged_in && (
        <LoadableDynamicAlertBanner
          variant={'danger'}
          show={
            !!user.validator_status &&
            user.validator_status !== KYC_VALIDATOR_STATUS.Success &&
            route?.id !== PagesName.RegisterPasskeyPage
          }
          message={KYC_MESSAGGE}
          buttonTitle={
            user.validator_status === KYC_VALIDATOR_STATUS.ShouldAnswerKBA &&
            ComponentSettings?.modals.KBAQuestions
              ? 'Update'
              : validatorNotOk &&
                  !window.__config__.componentSettings?.documentUpload
                ? 'Read more'
                : undefined
          }
          buttonOnClick={
            user.validator_status === KYC_VALIDATOR_STATUS.ShouldAnswerKBA &&
            ComponentSettings?.modals.KBAQuestions
              ? () => enableModal(ComponentName.QuestionsKBAModal)
              : () => enableModal(ComponentName.ValidationFailedModal)
          }
        />
      )}
      {!isFullScreen() ? (
        <>
          <PageContent
            rightSidebar={false}
            spacingClasses={headerNav.active && 'pt-xl-4'}
          >
            <>
              {children}
              {configLoaded === ConfigLoaded.Error && <NotFoundPage />}
            </>
          </PageContent>
        </>
      ) : (
        <>
          <>
            {route?.id === PagesName.CasinoInnerPage && children}
            {route?.id !== PagesName.CasinoInnerPage && (
              <div
                className={`d-flex flex-column py-2 w-100 ${user?.validator_status === 1 ? 'min-vh-100' : 'px-4'}`}
              >
                {children}
              </div>
            )}
          </>
          {configLoaded === ConfigLoaded.Error && <NotFoundPage />}
          <ErrorBoundary>
            {route?.id !== PagesName.CasinoInnerPage && <LoadablePageFooter />}
          </ErrorBoundary>
        </>
      )}
      <ErrorBoundary>
        <CookieConsent />
      </ErrorBoundary>
      <SelfExcludedCheck />
    </>
  );
};

export default PageLayout;
